import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { DeepDive, DeepDiveCard, DEEP_DIVE_CARD_MAX_WIDTH, ReviewCard } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, SxProps, Theme, Typography } from '@mui/material';

const STRINGS = appStrings.modules.deepDives;

export type ReviewCardConfig = Readonly<{
  quote: string;
  profileImage: string;
  name: string;
  subtitle: string;
}>;

type DeepDivesContentProps = Readonly<{
  deepDives: DeepDive[];
  reviewCardsByCategory?: Record<string, ReviewCardConfig>;
}>;

type DeepDivesByCategory = Record<string, DeepDive[]>;

export const DeepDivesContent = ({ deepDives, reviewCardsByCategory }: DeepDivesContentProps) => {
  const navigate = useNavigate();

  const handleCardClick = (deepDive: DeepDive) => {
    navigate(`${APP_ROUTE.DEEP_DIVES}/${deepDive.id}`);
  };

  const handleCtaClick = (deepDive: DeepDive) => {
    navigate(`${APP_ROUTE.DEEP_DIVES}/${deepDive.id}`);
  };

  // Group deep dives by category (excluding "Most Popular" which is rendered separately)
  const deepDivesByCategory = useMemo(() => {
    const grouped: DeepDivesByCategory = {};

    // Group by category name only (Most Popular is handled separately in parent)
    deepDives.forEach((dd) => {
      const categoryName = dd.category?.name;

      if (categoryName) {
        if (!grouped[categoryName]) {
          grouped[categoryName] = [];
        }

        grouped[categoryName].push(dd);
      }
    });

    return grouped;
  }, [deepDives]);

  // Order sections by the categories array (skip "Most Popular" at index 0)
  const orderedSections = useMemo(() => {
    return STRINGS.categories.slice(1).map((category) => ({
      title: category,
      deepDives: deepDivesByCategory[category] ?? [],
    }));
  }, [deepDivesByCategory]);

  if (deepDives.length === 0) {
    return (
      <Box sx={emptyStateSx}>
        <Typography variant="text-lg-semibold" sx={emptyTitleSx}>
          {STRINGS.emptyState.title}
        </Typography>
        <Typography variant="text-md-regular" sx={emptySubtitleSx}>
          {STRINGS.emptyState.subtitle}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={sectionsContainerSx}>
      {orderedSections
        .filter((section) => section.deepDives.length > 0)
        .map((section) => {
          const reviewCard = reviewCardsByCategory?.[section.title];

          return (
            <Box key={section.title} sx={sectionSx}>
              <Typography variant="text-md-bold" sx={sectionTitleSx}>
                {section.title}
              </Typography>
              <Box sx={gridSx}>
                {section.deepDives.map((deepDive) => (
                  <Box key={deepDive.id} sx={cardWrapperSx}>
                    <DeepDiveCard
                      deepDive={deepDive}
                      onCardClick={handleCardClick}
                      onCtaClick={handleCtaClick}
                    />
                  </Box>
                ))}
                {reviewCard && (
                  <Box sx={reviewCardWrapperSx}>
                    <ReviewCard
                      quote={reviewCard.quote}
                      profileImage={reviewCard.profileImage}
                      name={reviewCard.name}
                      subtitle={reviewCard.subtitle}
                      shrinkText={false}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
    </Box>
  );
};

const sectionsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
});

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const sectionTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-tertiary'],
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fill, ${DEEP_DIVE_CARD_MAX_WIDTH}px)`,
  gap: backrs.spacing['spacing-4xl'],
  mt: backrs.spacing['spacing-lg'],
});

const cardWrapperSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
};

const reviewCardWrapperSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  maxWidth: DEEP_DIVE_CARD_MAX_WIDTH,
};

const emptyStateSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: backrs.spacing['spacing-4xl'],
  gap: backrs.spacing['spacing-md'],
});

const emptyTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const emptySubtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
